// Copyright (c) Ping. All rights reserved.
//
// Best-effort DeviceGroup mirror for Activity reminders. Reminders are
// DeviceGroup-only personal state (§2.10 `0x94 REMINDER_SET` / `0x95
// REMINDER_CLEAR`): the local write through `webRemindersPersister` is the
// source of truth and ALWAYS happens first (so "remind me about this" works
// offline / before the SDK is ready); this helper additionally broadcasts the
// change to the user's OTHER devices over the DeviceGroup so a reminder set on
// one device follows the user everywhere and any device can fire it.
//
// Mirrors `apps/web/src/features/tasks/deviceGroupTaskSync.ts` field-for-field;
// every step is wrapped so a send failure NEVER surfaces to the caller — the
// receiving devices reconcile last-writer-wins on the next successful sync.

import { encodeEnvelopeJsonBytes, type ReminderRecord } from '@ping/ui';
import { bytesToHex } from '@ping/ui/src/conversation/mlsBytes';

import { log } from '@core/log/log';
import { resolveActiveConv } from '@features/chat/resolveActiveConv';

// Local mirror of the foundations `ReminderSetBody` wire shape (§2.10
// `0x94 REMINDER_SET`). The decoder in `appEvent/envelope.ts`
// (`decodeReminderSet`) reads exactly these keys.
interface ReminderSetWire {
  readonly reminder_id: string;
  readonly conversation_id: string;
  readonly fire_at: number;
  readonly created_at: number;
  readonly message_id?: string;
  readonly note?: string;
}

interface SdkClientLike {
  listConversations?: () => Promise<Array<{ id: Uint8Array; is_device_group: boolean }>>;
}

async function resolveDeviceGroupConvId(client: unknown): Promise<string | null> {
  const sdk = client as SdkClientLike;
  if (typeof sdk.listConversations !== 'function') return null;
  let convs: Array<{ id: Uint8Array; is_device_group: boolean }>;
  try {
    convs = await sdk.listConversations();
  } catch (err) {
    log.warn({
      event: 'reminders.deviceGroup.list_failed',
      message: (err as Error).message,
    });
    return null;
  }
  const dg = convs.find((c) => c.is_device_group);
  return dg ? bytesToHex(dg.id) : null;
}

/**
 * Mirror a reminder set to the DeviceGroup. No-op (logged) when there is no
 * client or no DeviceGroup conversation yet — the local persister write the
 * caller already performed remains authoritative.
 */
export async function sendReminderSet(
  client: unknown | null,
  record: ReminderRecord,
): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'reminders.deviceGroup.set.no_dg' });
      return;
    }
    const conv = await resolveActiveConv({ client, conversationId, conversationName: undefined });
    if (conv === null) return;
    const payload: ReminderSetWire = {
      reminder_id: record.id,
      conversation_id: record.conversationId,
      fire_at: record.fireAt,
      created_at: record.createdAt,
      ...(record.messageId !== undefined ? { message_id: record.messageId } : {}),
      ...(record.note !== undefined ? { note: record.note } : {}),
    };
    await conv.send(
      encodeEnvelopeJsonBytes({
        kind: 'com.ping.reminder_set',
        payload: payload as unknown as Record<string, unknown>,
      }),
    );
    log.info({ event: 'reminders.deviceGroup.set.sent', reminderIdPrefix: record.id.slice(0, 8) });
  } catch (err) {
    log.warn({ event: 'reminders.deviceGroup.set.failed', message: (err as Error).message });
  }
}

/** Mirror a reminder clear to the DeviceGroup. Same best-effort contract. */
export async function sendReminderClear(
  client: unknown | null,
  reminderId: string,
): Promise<void> {
  if (client === null) return;
  try {
    const conversationId = await resolveDeviceGroupConvId(client);
    if (conversationId === null) {
      log.info({ event: 'reminders.deviceGroup.clear.no_dg' });
      return;
    }
    const conv = await resolveActiveConv({ client, conversationId, conversationName: undefined });
    if (conv === null) return;
    await conv.send(
      encodeEnvelopeJsonBytes({
        kind: 'com.ping.reminder_clear',
        payload: { reminder_id: reminderId },
      }),
    );
    log.info({ event: 'reminders.deviceGroup.clear.sent', reminderIdPrefix: reminderId.slice(0, 8) });
  } catch (err) {
    log.warn({ event: 'reminders.deviceGroup.clear.failed', message: (err as Error).message });
  }
}
